import api from './api';

export type SatKind =
  | 'quiz' | 'exercise' | 'rent' | 'food' | 'purchase' | 'sale'
  | 'gift_in' | 'gift_out' | 'event' | 'bonus' | 'admin';

export const SAT_KIND_LABELS: Record<SatKind, string> = {
  quiz: 'Quiz', exercise: 'Exercice', rent: 'Loyer', food: 'Nourriture', purchase: 'Achat', sale: 'Vente',
  gift_in: 'Don reçu', gift_out: 'Don envoyé', event: 'Événement', bonus: 'Bonus', admin: 'Ajustement',
};

export interface SatTransaction {
  id: number;
  amount: number;
  kind: SatKind;
  note: string;
  created_at: string;
}

export interface SatTariff {
  S: number;
  Bq: number;
  /** ce que rapporte au plus un exercice parfait sur un SF inconnu */
  base_price: number;
  capacity: number;
  quota: number;
  capacity_override: number | null;
  balance: number;
  streak_bonus: number;
  quiz_today: number;
  quiz_daily_cap: number;
}

export interface SatEstimate {
  skill_id: number;
  skill_name: string;
  p: number;
  n_obs: number;
}

export interface SatPet {
  id: number;
  name: string;
  species: 'cat' | 'fox' | 'elephant';
  species_label: string;
  state: 'alive' | 'dead' | 'immortal';
  stage: string;
  age_weeks: number;
  mood: 'content' | 'hungry' | 'scared' | 'danger';
  food_rations: number;
  hunger_days: number;
  homeless_days: number;
  housing_key: string;
  born_at: string;
  balance: number;
}

export interface SatGrave {
  id: number;
  name: string;
  species: 'cat' | 'fox' | 'elephant';
  species_label: string;
  born_at: string;
  died_at: string;
  death_cause: 'hunger' | 'street';
  balance: number;
}

export const SAT_MOOD_LABELS: Record<SatPet['mood'], string> = {
  content: 'content', hungry: 'a faim', scared: 'terrifié (à la rue)', danger: 'en danger',
};

export interface SatStudent {
  balance: number;
  tariff: SatTariff;
  ledger: SatTransaction[];
  capacity: { quota: number; capacity: number; capacity_override: number | null };
  estimates: SatEstimate[];
  pet: SatPet | null;
  cemetery: SatGrave[];
}

export interface SatTransfer extends SatTransaction {
  student_id: number;
  student_name: string;
}

const satService = {
  async getStudent(id: number): Promise<SatStudent> {
    return (await api.get(`/sat/students/${id}/`)).data;
  },
  async adjust(id: number, amount: number, note: string): Promise<SatStudent> {
    return (await api.post(`/sat/students/${id}/adjust/`, { amount, note })).data;
  },
  async setCapacity(id: number, capacityOverride: number | null): Promise<SatStudent> {
    return (await api.post(`/sat/students/${id}/capacity/`, { capacity_override: capacityOverride })).data;
  },
  /** Résurrection exceptionnelle d'un animal mort (bug de barème) ; refusée s'il existe un animal vivant. */
  async revive(id: number, petId: number): Promise<SatStudent> {
    return (await api.post(`/sat/students/${id}/revive/`, { pet_id: petId })).data;
  },
  async getTransfers(week?: string): Promise<SatTransfer[]> {
    return (await api.get('/sat/transfers/', { params: week ? { week } : {} })).data;
  },
  async getSettings(): Promise<Record<string, string>> {
    return (await api.get('/sat/settings/')).data;
  },
  async putSettings(values: Record<string, string | number>): Promise<Record<string, string>> {
    return (await api.put('/sat/settings/', values)).data;
  },
};

export default satService;
